import { ApolloError, FetchPolicy, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { notNil } from '../notNil';
import { useCurrentBusinessId } from './useCurrentBusinessId';

interface UseProfileProps {
  fetchPolicy?: FetchPolicy;
  skip?: boolean;
}

interface UseProfileResult {
  profile: MBAPI.Profile | undefined;
  businesses: MBAPI.Business[] | undefined;
  refetch: VoidFunction;
  error?: ApolloError;
  loading: boolean;
}

const defaultProps: UseProfileProps = {
  fetchPolicy: 'cache-first',
  skip: false,
};

export function useProfile({
  fetchPolicy,
  skip,
}: UseProfileProps = defaultProps): UseProfileResult {
  const { data, refetch, error, loading } = useQuery<MBAPI.ProfileQuery>(Queries.user.profile, {
    fetchPolicy,
    skip,
  });

  return useMemo(() => {
    return {
      profile: data?.me ?? undefined,
      businesses: data?.me?.businesses.filter(notNil) ?? undefined,
      refetch,
      error,
      loading,
    };
  }, [data, refetch, error, loading]);
}

export function useCurrentBusiness(): MBAPI.Business | undefined {
  const businessId = useCurrentBusinessId();
  const { businesses } = useProfile();
  return businesses?.find(b => b.id === businessId);
}
